import { get } from 'lodash';
import { FileText, Film, Link2, MoreHorizontal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  convertImageFileWithCloudinary,
  IMAGE_QUALITY_OPTIONS
} from 'lib/media/imageHelper';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { ImagePreview } from 'v5/design-system/ImagePreview';
import { PinMedia } from 'v5/patterns/widgets/PinMedia/PinMedia';
import { pinMediaKindOf } from 'v5/patterns/widgets/PinMedia/pinMediaKind';
import {
  MediaColumnLayout,
  MediaDialog,
  MediaDisplay,
  MediaSidebar,
  MediaThumbnails,
  MediaToolbar
} from 'v5/design-system/MediaDialog';
import {
  PinViewerNavigationLockContext,
  type PinViewerNavigationLock
} from './pinViewerNavigationLock';

/** What the viewer needs of a pin; the sidebar may read more. */
export type PinViewerPin = {
  id: number;
  url?: string | null;
  pin_type?: string | null;
  file?: unknown;
};

function pinName(pin: PinViewerPin): string | undefined {
  return get(pin, 'file.name') || pin.url || undefined;
}

/** The resized picture; the original if that fails to load. */
function PinPicture({
  resized,
  original,
  alt
}: {
  resized: string;
  original: string;
  alt: string;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <MediaDisplay.Picture
      src={failed ? original : resized}
      alt={alt}
      onError={() => {
        if (resized !== original) setFailed(true);
      }}
    />
  );
}

/** A pin in the strip of thumbnails: its picture, or the kind of what it is. */
function PinThumbnail({ pin }: { pin: PinViewerPin }) {
  const icon = 'p-3 text-fg-default/80';
  switch (pinMediaKindOf(pin)) {
    case 'image': {
      const original = pin.url ?? '';
      return (
        <ImagePreview
          src={
            convertImageFileWithCloudinary(
              original,
              IMAGE_QUALITY_OPTIONS.small
            ) || original
          }
          className="bg-transparent"
        />
      );
    }
    case 'video':
      return <Film className={icon} aria-hidden />;
    case 'file':
    case 'provider-file':
      return <FileText className={icon} aria-hidden />;
    default:
      return <Link2 className={icon} aria-hidden />;
  }
}

/**
 * The pins of one collection at full size, one at a time, with the previous /
 * next one a click or an arrow key away, and a strip of thumbnails. Images
 * show over their blurred backdrop; videos, files and links show as
 * `PinItem` does. The `sidebar` sits on a card beside the media, which ends
 * in the position of the pin and the toggle of the thumbnails (T). Controlled:
 * the host owns which pin is shown; a `currentId` not among `pins` shows the
 * first one. A part of the sidebar can keep it on the pin shown, and open,
 * meanwhile (`useLockPinViewerNavigation`).
 */
export function PinViewer({
  pins,
  open,
  onOpenChange,
  currentId,
  onCurrentIdChange,
  sidebar,
  onOpenAutoFocus,
  footerActions,
  footerMenu,
  onCloseAutoFocus,
  total
}: {
  /** In the order shown. */
  pins: PinViewerPin[];
  open: boolean;
  onOpenChange(open: boolean): void;
  currentId: PinViewerPin['id'] | undefined;
  onCurrentIdChange(id: PinViewerPin['id']): void;
  /** Gets the pin shown; none while `pins` is empty. */
  sidebar(pin: PinViewerPin | undefined): ReactNode;
  /** As the viewer opens, its content mounted; prevented, nothing in it takes the focus. */
  onOpenAutoFocus?: (event: Event) => void;
  /** The host's actions in the sidebar's footer, beside the counter. */
  footerActions?: ReactNode;
  /**
   * The host's `DropdownMenuItem`s for one "More actions" button after its footer actions; none,
   * no button.
   */
  footerMenu?: ReactNode;
  /** Where the focus goes back to; without it, or not prevented, to what opened it. */
  onCloseAutoFocus?: (event: Event) => void;
  /**
   * How many pins the collection holds when `pins` holds only the first of them, the rest read
   * after: the thumbnails, the arrows and the counter wait for the whole, not for what is read.
   */
  total?: number;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'PinViewer' });
  const [thumbnailsShown, setThumbnailsShown] = useState(true);
  const [navigationLock, setNavigationLock] =
    useState<PinViewerNavigationLock | null>(null);
  const navigationLocked = navigationLock !== null;
  const count = pins.length;
  // a collection read in parts keeps its strip while the rest comes in
  const pictureCount = Math.max(count, total ?? count);
  const multiple = count > 0 && pictureCount > 1;
  const index =
    count === 0
      ? null
      : Math.max(
          0,
          pins.findIndex(p => p.id === currentId)
        );
  const pin = index === null ? undefined : pins[index];
  const go = (step: number) => {
    if (index === null) return;
    onCurrentIdChange(pins[(index + step + count) % count].id);
  };
  const name = (pin && pinName(pin)) ?? t(pin ? 'untitled' : 'empty');
  const original = pin?.url ?? '';
  const isImage = pin !== undefined && pinMediaKindOf(pin) === 'image';
  const resized = isImage
    ? convertImageFileWithCloudinary(original, IMAGE_QUALITY_OPTIONS.large) ||
      original
    : null;

  return (
    <MediaDialog
      open={open}
      onOpenAutoFocus={onOpenAutoFocus}
      onCloseAutoFocus={onCloseAutoFocus}
      onOpenChange={next => {
        if (!next && navigationLocked) return;
        onOpenChange(next);
      }}
      onEscapeKeyDown={event => {
        if (!navigationLock) return;
        // The part holding the pin lets go first, asking when it must.
        event.preventDefault();
        navigationLock.requestLeave();
      }}
    >
      <MediaDialog.Scene
        subject={pin === undefined ? 'empty' : String(pin.id)}
        backdrop={resized}
      >
        {pin === undefined && (
          <p className="text-sm text-fg-default/70">{t('empty')}</p>
        )}
        {pin !== undefined &&
          (resized !== null ? (
            <PinPicture resized={resized} original={original} alt={name} />
          ) : (
            <div className="absolute inset-4 flex items-center justify-center sm:inset-x-22">
              <div className="max-h-full w-full max-w-4xl overflow-auto rounded-xl">
                <PinMedia pin={pin} autoPlay />
              </div>
            </div>
          ))}
      </MediaDialog.Scene>
      <MediaColumnLayout>
        <MediaDisplay>
          {multiple && (
            <MediaDisplay.Navigation
              disabled={navigationLocked}
              onPrevious={() => go(-1)}
              onNext={() => go(1)}
            />
          )}
          <MediaDisplay.Close disabled={navigationLocked} />
        </MediaDisplay>
        {multiple && (
          <MediaThumbnails shown={thumbnailsShown} aria-label={t('listLabel')}>
            {pins.map(item => (
              <MediaThumbnails.Item
                key={item.id}
                current={item.id === pin?.id}
                aria-label={pinName(item) ?? t('untitled')}
                disabled={navigationLocked}
                onClick={() => onCurrentIdChange(item.id)}
              >
                <PinThumbnail pin={item} />
              </MediaThumbnails.Item>
            ))}
          </MediaThumbnails>
        )}
        <MediaDialog.Title className="sr-only">{name}</MediaDialog.Title>
      </MediaColumnLayout>
      <MediaSidebar>
        <MediaSidebar.Body>
          <PinViewerNavigationLockContext.Provider value={setNavigationLock}>
            {sidebar(pin)}
          </PinViewerNavigationLockContext.Provider>
        </MediaSidebar.Body>
        {((index !== null && multiple) ||
          footerActions !== undefined ||
          footerMenu !== undefined) && (
          <MediaSidebar.Footer className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-border-default py-2">
            {index !== null && multiple && (
              <MediaToolbar.Counter index={index} count={pictureCount} />
            )}
            {footerActions !== undefined || footerMenu !== undefined ? (
              // the host's actions and the toggle, together at the footer's end
              <span className="ml-auto flex flex-wrap items-center justify-end gap-1">
                {footerActions}
                {footerMenu !== undefined && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        layout="icon"
                        aria-label={t('moreActions')}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {footerMenu}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {multiple && (
                  <MediaThumbnails.Toggle
                    shown={thumbnailsShown}
                    onShownChange={setThumbnailsShown}
                  />
                )}
              </span>
            ) : (
              <MediaThumbnails.Toggle
                shown={thumbnailsShown}
                onShownChange={setThumbnailsShown}
              />
            )}
          </MediaSidebar.Footer>
        )}
      </MediaSidebar>
    </MediaDialog>
  );
}
